<template>
  <div class="card">
    <div class="card-body">
      <h5 class="card-title mb-4">Обновление справочников маркетплейсов</h5>

      <div class="row g-4">
        <div class="col-md-6">
          <label class="form-label" for="mpInfoStore">Магазин</label>
          <select
              id="mpInfoStore"
              class="form-select"
              v-model="selectedStoreId"
              :disabled="running"
              @change="onStoreChange">
            <option value="" disabled>Выберите магазин</option>
            <option v-for="store in supportedStores" :key="store.id" :value="store.id">
              {{ store.name }} — {{ marketplaceTitle(store.marketplace) }}
            </option>
          </select>
        </div>

        <div class="col-md-6">
          <label class="form-label" for="mpInfoType">Что обновить</label>
          <select
              id="mpInfoType"
              class="form-select"
              v-model="selectedInfoType"
              :disabled="running || !availableInfoTypes.length">
            <option value="" disabled>Выберите тип данных</option>
            <option v-for="type in availableInfoTypes" :key="type.value" :value="type.value">
              {{ type.label }}
            </option>
          </select>
        </div>
      </div>

      <p class="text-muted mt-3 mb-4">
        Выберите магазин, от имени которого обновить данные. Обновлённый справочник
        станет доступен всем магазинам этого маркетплейса.
      </p>

      <div v-if="selectedInfoType === 'wb_tnved'" class="alert alert-info" role="alert">
        Запускайте после обновления «Предметы (категории) WB»: коды ТН ВЭД загружаются по всем предметам.
        Обновление займёт около часа (лимит WB — 100 запросов в минуту).
      </div>

      <div v-if="!supportedStores.length" class="alert alert-warning" role="alert">
        Нет магазинов Wildberries или Ozon. Обновление справочников доступно только для них.
      </div>

      <ButtonComponent
          ref="runButton"
          :class="'btn btn-primary'"
          :disabled="!canRun"
          @click="run">
        Запустить обновление
      </ButtonComponent>

      <!-- Task progress -->
      <div v-if="task" class="mt-4">
        <hr class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="mb-0">{{ task.name || 'Задача обновления' }}</h6>
          <span class="badge" :class="resultBadgeClass">{{ resultDescription }}</span>
        </div>

        <div class="progress mb-2" style="height: 20px;">
          <div
              class="progress-bar"
              :class="{'progress-bar-striped progress-bar-animated': isInProgress}"
              role="progressbar"
              :style="{width: progress + '%'}"
              :aria-valuenow="progress"
              aria-valuemin="0"
              aria-valuemax="100">
            {{ progress }} %
          </div>
        </div>

        <p v-if="task.task_info" class="mb-0" :class="isFailure ? 'text-danger' : 'text-muted'">
          {{ task.task_info }}
        </p>

        <RouterLink
            v-if="task.id"
            :to="{name: 'task-detail', params: {id: task.id}}"
            class="d-inline-block mt-2">
          Открыть задачу
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<script>
import ButtonComponent from "@/components/tools/Button.vue";
import {Message, Messages} from "@/data/messages";
import {Marketplaces} from "@/data/store";
import {updateMarketplaceInfo} from "@/api/store";
import {getInfo} from "@/api/task";

const POLL_INTERVAL = 4000

const INFO_TYPES = {
  wb: [
    {value: 'wb_objects', label: 'Предметы (категории) WB'},
    {value: 'wb_objects_attributes', label: 'Характеристики предметов WB'},
    {value: 'wb_tnved', label: 'ТН ВЭД WB'},
    {value: 'wb_colors', label: 'Цвета WB'},
  ],
  ozon: [
    {value: 'ozon_types', label: 'Типы Ozon'},
    {value: 'ozon_attributes', label: 'Атрибуты Ozon'},
    {value: 'ozon_attributes_values', label: 'Значения атрибутов Ozon'},
  ],
}

const RESULT_DESCRIPTIONS = {
  'in_progress': 'В процессе',
  'success': 'Успешно выполнена',
  'failure': 'Произошла ошибка',
}

export default {
  name: 'MarketplaceInfoUpdate',
  components: {ButtonComponent},
  props: {
    stores: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedStoreId: '',
      selectedInfoType: '',
      running: false,
      task: null,
      pollTimer: null,
    }
  },
  computed: {
    supportedStores() {
      return this.stores.filter((store) => INFO_TYPES[store.marketplace])
    },
    selectedStore() {
      return this.stores.find((store) => store.id === this.selectedStoreId)
    },
    availableInfoTypes() {
      return INFO_TYPES[this.selectedStore?.marketplace] || []
    },
    canRun() {
      return !!this.selectedStoreId && !!this.selectedInfoType && !this.running
    },
    isInProgress() {
      return this.task?.task_result === 'in_progress'
    },
    isFailure() {
      return this.task?.task_result === 'failure'
    },
    progress() {
      return this.task?.task_progress ?? 0
    },
    resultDescription() {
      return RESULT_DESCRIPTIONS[this.task?.task_result] || this.task?.task_result || ''
    },
    resultBadgeClass() {
      const map = {
        'in_progress': 'bg-label-info',
        'success': 'bg-label-success',
        'failure': 'bg-label-danger',
      }
      return map[this.task?.task_result] || 'bg-label-secondary'
    },
  },
  methods: {
    marketplaceTitle(code) {
      return Marketplaces[code]?.name || code
    },
    onStoreChange() {
      this.selectedInfoType = ''
    },
    async run() {
      if (!this.canRun) {
        return
      }
      this.running = true
      this.$refs.runButton.startLoading()
      const message = new Message()
      try {
        const result = await updateMarketplaceInfo(this.selectedStoreId, this.selectedInfoType)
        this.task = result.data
        if (this.isInProgress) {
          this.startPolling()
        } else {
          this.running = false
        }
      } catch (exception) {
        this.running = false
        message.type = 'failure'
        message.text = exception.response?.data?.detail || 'Не удалось запустить обновление'
        Messages.list.push(message)
      } finally {
        this.$refs.runButton.endLoading()
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(async () => {
        try {
          const result = await getInfo(this.task.id)
          this.task = result.data
          if (!this.isInProgress) {
            this.stopPolling()
            this.running = false
            this.notifyResult()
          }
        } catch (e) {
          this.stopPolling()
          this.running = false
        }
      }, POLL_INTERVAL)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    notifyResult() {
      const message = new Message()
      if (this.task?.task_result === 'success') {
        message.type = 'success'
        message.autoremove = 10
        message.text = this.task.task_info || 'Информация из маркетплейса обновлена'
      } else {
        message.type = 'failure'
        message.text = this.task?.task_info || 'Произошла ошибка при обновлении'
      }
      Messages.list.push(message)
    },
  },
  beforeUnmount() {
    this.stopPolling()
  },
}
</script>
